import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Nav from "../components/app/Nav";
import Footer from "../components/app/Footer";
import FloatingButton from "../components/app/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWarning,
  faTimes,
  faBolt,
  faCheck,
  faCircleInfo,
  faTriangleExclamation,
  faCheckCircle,
  faArrowLeft,
  faPhone,
  faChevronDown,
  faChevronUp,
  faPlane,
  faShip,
  faBan,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/app/ProhibitedItemsPage.css";

interface UniversalProhibition {
  category: string;
  air: string[];
  maritime: string;
}

interface CountryRestriction {
  category: string;
  air: string[];
  maritime: string[];
  absolute: string[];
}

interface PeruRestriction {
  category: string;
  productType: string;
  legalStatus: string;
  regulator: string;
  requirements: string;
}

interface CountryData {
  name: string;
  code: string;
  restrictions?: CountryRestriction[];
  peruRestrictions?: PeruRestriction[];
}

const universalProhibitions: UniversalProhibition[] = [
  {
    category: "Inflamables y Químicos",
    air: [
      "Perfumes, colonias, esmaltes de uñas, acetona.",
      "Aerosoles (desodorantes, insecticidas).",
      "Pinturas, licores de alta graduación, pegamentos fuertes.",
    ],
    maritime:
      "Se permiten por vía marítima bajo la modalidad de carga consolidada regular sin penalizaciones de seguridad aérea.",
  },
  {
    category: "Baterías de Litio Sueltas",
    air: [
      "Powerbanks (baterías externas).",
      "Pilas de litio sueltas.",
      "Baterías de repuesto para laptops o herramientas fuera de su equipo original.",
    ],
    maritime:
      "Las baterías dentro de sus equipos (ej. un celular nuevo) pueden viajar por avión bajo declaración. Sueltas solo se permiten por vía marítima bajo embalaje adecuado.",
  },
  {
    category: "Dinero y Valores",
    air: [
      "Monedas, billetes legítimos de cualquier divisa.",
      "Cheques al portador.",
      "Lingotes de oro/plata, joyería fina de muy alto valor, piedras preciosas.",
    ],
    maritime:
      "Totalmente prohibido en ambas modalidades por riesgo de robo y regulaciones contra el lavado de activos. Requiere transportes de valores especializados.",
  },
  {
    category: "Gases y Presurizados",
    air: [
      "Amortiguadores a gas.",
      "Tanques de buceo (incluso vacíos si no están certificados).",
      "Extintores, cartuchos de CO2.",
    ],
    maritime:
      "Los amortiguadores y repuestos bajo presión se envían exclusivamente por vía marítima.",
  },
  {
    category: "Falsificaciones / Piratería",
    air: [
      "Artículos réplica.",
      "Ropa o calzado de marcas falsificadas (imitaciones de lujo).",
      "Software o películas pirata.",
    ],
    maritime:
      "Riesgo de decomiso absoluto en las aduanas de salida de EE. UU. y en las aduanas de destino de todos los países de la región.",
  },
  {
    category: "Seguridad y Armas",
    air: [
      "Armas de fuego, municiones, partes de armas y pólvora.",
      "Cualquier objeto que parezca o sirva como arma (incluye ballestas, arcos, flechas, cuchillos de caza y gas pimienta).",
      "Gas pimienta, tasers, miras telescópicas, chalecos antibalas, cascos, masks antigás, cuchillos tácticos, balines, armas de juguete/Airsoft/Paintball.",
    ],
    maritime:
      "Cualquier artículo táctico o de defensa personal detectado genera confiscación y problemas legales.",
  },
];

const countriesData: CountryData[] = [
  {
    name: "Venezuela",
    code: "ve",
    restrictions: [
      {
        category: "1. Tecnología y Electrónicos",
        air: [
          "Equipos de criptominería (ASICs, rigs) y tarjetas de video de alta gama pueden ser retenidos si aduana presume uso comercial.",
          "Teléfonos y laptops permitidos (máx. 2 o 3 por envío para evitar presunción comercial).",
        ],
        maritime: [
          "Mismas restricciones que el aéreo para criptominería.",
          "Pantallas grandes y plantas eléctricas permitidas (pagan arancel por peso/volumen).",
        ],
        absolute: [],
      },
      {
        category: "2. Salud, Cosmética y Alimentos",
        air: [
          "Alimentos no perecederos (enlatados, secos) permitidos sin problema.",
          "Medicinas permitidas (preferible con récipe/receta médica si son cajas grandes).",
        ],
        maritime: [
          "Alimentos en volumen (cajas de comida) son el rubro más común y no tienen trabas sanitarias graves actualmente.",
        ],
        absolute: [],
      },
      {
        category: "3. Moda y Artículos Usados",
        air: ["Ropa y calzado nuevo permitido."],
        maritime: [
          "Ropa usada permitida (el envío de ropa usada de familiares es común y plenamente aceptado).",
        ],
        absolute: [
          "Ropa, botas o accesorios de diseño militar o camuflado (tácticos).",
        ],
      },
      {
        category: "4. Automotriz y Herramientas",
        air: ["Repuestos nuevos permitidos."],
        maritime: [
          "Repuestos usados (motores, cajas, transmisiones) y amortiguadores se permiten ÚNICAMENTE por vía marítima.",
        ],
        absolute: [],
      },
    ],
  },
  {
    name: "República Dominicana",
    code: "do",
    restrictions: [
      {
        category: "1. Tecnología y Electrónicos",
        air: [
          "Celulares requieren homologación si son marcas no registradas en INDOTEL.",
          "Laptops y consolas entran libres de impuestos si el valor total de la orden es menor a $200 USD.",
        ],
        maritime: [
          "Equipos electrónicos usados en grandes cantidades (computadoras reacondicionadas) requieren permisos comerciales de importación.",
        ],
        absolute: [],
      },
      {
        category: "2. Salud, Cosmética y Alimentos",
        air: [
          "Suplementos y vitaminas permitidos para uso personal en cantidades bajas (pocas unidades).",
        ],
        maritime: [],
        absolute: [
          "Medicamentos controlados o psicotrópicos.",
          "Alimentos perecederos o que requieran refrigeración.",
        ],
      },
      {
        category: "3. Moda y Artículos Usados",
        air: ["Ropa y calzado nuevo permitido sin problemas."],
        maritime: [
          "El envío de ropa usada en tanques/cajas para familiares es tolerado, pero si se detecta fin comercial masivo se decomisa.",
        ],
        absolute: [
          "Ropa usada en cantidades comerciales está prohibida por leyes textiles locales.",
        ],
      },
      {
        category: "4. Automotriz y Herramientas",
        air: [
          "Repuestos nuevos permitidos.",
          "Herramientas eléctricas con baterías de litio integradas deben viajar por canales aéreos especiales (mercancía peligrosa).",
        ],
        maritime: [
          "Los repuestos usados grandes tienen restricciones de aduana según el año del vehículo.",
        ],
        absolute: ["Neumáticos (gomas) usados."],
      },
      {
        category: "5. Seguridad y Armas",
        air: [],
        maritime: [
          "Cuchillos de cocina o herramientas de camping se permiten por vía marítima.",
          "Réplicas (Airsoft) requieren permisos especiales del Ministerio de Defensa.",
        ],
        absolute: [],
      },
    ],
  },
  /*
    {
        name: "Cuba",
        code: "cu",
        restrictions: [
            {
                category: "1. Tecnología y Electrónicos",
                air: [
                    "Celulares y laptops limitados a máx. 2 por persona."
                ],
                maritime: [
                    "Electrodomésticos estándar permitidos bajo arancel estricto."
                ],
                absolute: [
                    "Drones, routers potentes, switches, access points, teléfonos satelitales, GPS independientes y smartwatches con conectividad celular independiente.",
                    "Plantas eléctricas de más de 1500W, aires acondicionados grandes, motores de alta potencia."
                ]
            },
            {
                category: "2. Salud, Cosmética y Alimentos",
                air: [
                    "Alimentos, medicinas and aseo (MIA) están exentos de aranceles bajo condiciones específicas de peso."
                ],
                maritime: [
                    "Se permiten envíos masivos de comida seca, latas y productos de aseo en cajas de carácter humanitario."
                ],
                absolute: [
                    "Medicamentos con efectos estupefacientes o psicotrópicos."
                ]
            },
            {
                category: "3. Moda y Artículos Usados",
                air: [
                    "Ropa y calzado permitidos.",
                    "Se pesan rigurosamente en la aduana de destino para calcular el impuesto si excede el límite exento de regalo."
                ],
                maritime: [
                    "Se permiten pacas de ropa y calzado bajo el esquema general de misceláneos."
                ],
                absolute: []
            },
            {
                category: "4. Automotriz y Herramientas",
                air: [
                    "Repuestos ligeros de auto/moto permitidos (bujías, pastillas de freno, juntas, cables)."
                ],
                maritime: [
                    "Neumáticos nuevos permitidos con límite de unidades."
                ],
                absolute: [
                    "Motores de combustión completos, chasis o estructuras integrales de vehículos."
                ]
            },
            {
                category: "5. Seguridad y Armas",
                air: [],
                maritime: [
                    "Regulaciones de seguridad extremas. Inspección manual del 100% de la carga."
                ],
                absolute: []
            }
        ]
    },
    */
  {
    name: "Costa Rica",
    code: "cr",
    restrictions: [
      {
        category: "1. Tecnología y Electrónicos",
        air: [
          "Dispositivos médicos del hogar (glucómetros, termómetros infrarrojos) requieren permiso del Ministerio de Salud.",
          "Radios de comunicación bidireccional (walkie-talkies) requieren permiso de Sutel.",
        ],
        maritime: [
          "Equipos industriales o motores pesados requieren un desglose de partida arancelaria detallado y pago de impuestos regular.",
        ],
        absolute: [],
      },
      {
        category: "2. Salud, Cosmética y Alimentos",
        air: [
          "MUY ESTRICTO: Cosméticos (maquillaje, cremas), suplementos (proteínas, vitaminas) y alimentos requieren Registro Sanitario si exceden cantidades de uso personal (más de 3 piezas del mismo tipo).",
        ],
        maritime: [
          "Mismas restricciones. No se pueden consolidar ni importar alimentos comerciales sin los permisos previos del Ministerio de Salud.",
        ],
        absolute: [],
      },
      {
        category: "3. Moda y Artículos Usados",
        air: ["Ropa y calzado nuevo permitido."],
        maritime: [
          "Mismas restricciones estrictas para ropa usada por razones de salud pública.",
        ],
        absolute: [
          "Ropa usada (exige un certificado de fumigación/desinfección formal emitido en EE. UU. que los couriers comunes no tramitan).",
        ],
      },
      {
        category: "4. Automotriz y Herramientas",
        air: [
          "Repuestos nuevos y herramientas permitidos pagando el impuesto respectivo.",
        ],
        maritime: [
          "Repuestos usados pesados deben demostrar que no son chatarra.",
        ],
        absolute: [
          "Llantas/neumáticos usados (por razones ambientales y de salud pública).",
        ],
      },
      {
        category: "5. Seguridad y Armas",
        air: [],
        maritime: [
          "Artículos de camping o cuchillos permitidos por mar.",
          "Artículos de defensa (gas pimienta) retenidos de forma regular.",
        ],
        absolute: [],
      },
    ],
  },
  {
    name: "Guatemala",
    code: "gt",
    restrictions: [
      {
        category: "1. Tecnología y Electrónicos",
        air: [
          "Teléfonos celulares deben registrarse con el IMEI (los casilleros lo exigen para verificar que no estén reportados como robados).",
          "Drones pequeños permitidos para uso recreativo.",
        ],
        maritime: [
          "Maquinaria pesada o pantallas de TV gigantes entran sin problema pagando el IVA e impuestos correspondientes.",
        ],
        absolute: [],
      },
      {
        category: "2. Salud, Cosmética y Alimentos",
        air: [
          "Vitaminas, suplementos y alimentos procesados debidamente sellados están permitidos en cantidades bajas.",
        ],
        maritime: [],
        absolute: [
          "Semillas, plantas vivas, tierra, madera en bruto o productos animales no procesados (requieren estrictos permisos fitosanitarios).",
        ],
      },
      {
        category: "3. Moda y Artículos Usados",
        air: [
          "Ropa y zapatos nuevos permitidos sin restricciones más allá del pago del impuesto general de aduana.",
        ],
        maritime: [
          "Las pacas de ropa usada con fines comerciales requieren licencias de importación especiales y pago de aranceles específicos.",
        ],
        absolute: [],
      },
      {
        category: "4. Automotriz y Herramientas",
        air: [
          "Repuestos nuevos y herramientas eléctricas o manuales permitidas.",
        ],
        maritime: [
          "Motores usados y repuestos grandes permitidos por mar (Guatemala cuenta con un amplio mercado de importación de piezas desde EE. UU.).",
        ],
        absolute: [],
      },
      {
        category: "5. Seguridad y Armas",
        air: [],
        maritime: [
          "Herramientas de corte o machetes recreativos permitidos solo por vía marítima.",
        ],
        absolute: [],
      },
    ],
  },
  {
    name: "Perú",
    code: "pe",
    peruRestrictions: [
      {
        category: "1. Tecnología Estándar",
        productType:
          "Laptops, tablets, audífonos de cable, consolas de videojuegos y memorias",
        legalStatus: "Permitido",
        regulator: "SUNAT",
        requirements: "Libre ingreso siempre que no utilicen redes celulares.",
      },
      {
        category: "2. Telecomunicaciones",
        productType: "Celulares y Smartwatches con ranura SIM (Nuevos)",
        legalStatus: "Restringido",
        regulator: "MTC",
        requirements:
          "Requiere Permiso de Internamiento Digital. Máximo 1 unidad por envío.",
      },
      {
        category: "3. Salud y Belleza",
        productType:
          "Medicinas, vitaminas, suplementos alimenticios y proteínas",
        legalStatus: "Restringido",
        regulator: "DIGEMID",
        requirements:
          "Requiere receta médica autorizada o visado. Límite de uso personal.",
      },
      {
        category: "4. Salud y Belleza",
        productType:
          "Maquillaje, cremas corporales, perfumes, jabones y champús",
        legalStatus: "Restringido",
        regulator: "DIGEMID",
        requirements:
          "Regulado como cosmético. Límite usual de hasta 4 unidades por envío.",
      },
      {
        category: "5. Moda y Calzado",
        productType: "Ropa, zapatos, carteras y maletas completamente nuevos",
        legalStatus: "Permitido",
        regulator: "SUNAT",
        requirements: "Libre ingreso para uso personal. No requiere permisos.",
      },
      {
        category: "6. Moda y Calzado",
        productType: "Ropa, zapatillas, sábanas o alfombras de segundo uso",
        legalStatus: "Prohibido",
        regulator: "SUNAT / MINSA",
        requirements:
          "Prohibición total por razones de salud pública y bioseguridad.",
      },
      {
        category: "7. Automotriz",
        productType:
          "Autopartes, motores, neumáticos y repuestos mecánicos usados",
        legalStatus: "Prohibido",
        regulator: "SUNAT / MTC",
        requirements:
          "Prohibición total de ingreso para componentes vehiculares de segundo uso.",
      },
      {
        category: "8. Seguridad y Armas",
        productType:
          "Armas de fuego, municiones, explosivos y cuchillos tácticos",
        legalStatus: "Restringido",
        regulator: "SUCAMEC",
        requirements:
          "Altamente regulado. Requiere licencias y permisos especiales de importación.",
      },
      {
        category: "9. Hogar y Oficina",
        productType:
          "Adornos, utensilios de cocina manuales, herramientas, libros y útiles",
        legalStatus: "Permitido",
        regulator: "SUNAT",
        requirements: "Libre ingreso para uso personal o doméstico.",
      },
      {
        category: "10. Infantil y Cuidado",
        productType: "Juguetes para niños y artículos de estimulación temprana",
        legalStatus: "Restringido",
        regulator: "DIGESA",
        requirements:
          "Requiere autorización por control de toxicidad en pinturas y materiales.",
      },
      {
        category: "11. Infantil y Cuidado",
        productType: "Juguetes con formas de armas de fuego (réplicas bélicas)",
        legalStatus: "Prohibido",
        regulator: "SUCAMEC",
        requirements: "Prohibición total por seguridad nacional.",
      },
      {
        category: "12. Alimentos",
        productType: "Alimentos procesados, empaquetados y golosinas",
        legalStatus: "Restringido",
        regulator: "DIGESA",
        requirements:
          "Requiere registro sanitario vigente o declaración jurada de uso personal.",
      },
      {
        category: "13. Agrario y Veterinaria",
        productType:
          "Semillas, plantas vivas, snacks para mascotas and embutidos",
        legalStatus: "Restringido",
        regulator: "SENASA",
        requirements:
          "Requiere Certificado Fito o Zoosanitario para evitar plagas.",
      },
      {
        category: "14. Licores",
        productType: 'Bebidas alcohólicas extranjeras etiquetadas como "Pisco"',
        legalStatus: "Prohibido",
        regulator: "INDECOPI",
        requirements:
          "Prohibido por ley para proteger la denominación de origen peruana.",
      },
      {
        category: "15. Químicos y Peligrosos",
        productType:
          "Aerosoles, desodorantes en spray y esmaltes de uñas inflamables",
        legalStatus: "Prohibido",
        regulator: "SUNAT / Courier",
        requirements:
          "Mercancía peligrosa (Hazmat) por riesgo de inflamabilidad en el avión.",
      },
      {
        category: "16. Químicos y Peligrosos",
        productType:
          "Baterías de litio sueltas (Powerbanks o fuera de un equipo)",
        legalStatus: "Prohibido",
        regulator: "IATA / Courier",
        requirements:
          "Prohibidas en transporte aéreo de carga general por riesgo de explosión.",
      },
    ],
  },
];

const ProhibitedItemsPage: React.FC = () => {
  const [openCountry, setOpenCountry] = useState<string | null>("Venezuela");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleToggleCountry = (countryName: string) => {
    setOpenCountry(openCountry === countryName ? null : countryName);
  };

  return (
    <>
      <Helmet>
        <title>Artículos Prohibidos y Restricciones | elPaket</title>
        <meta
          name="description"
          content="Guía completa de artículos prohibidos, mercancías peligrosas y restricciones aduanales por país para envíos internacionales desde EE. UU."
        />
        <meta
          name="keywords"
          content="artículos prohibidos envíos Venezuela, envíos República Dominicana prohibidos, qué no enviar Costa Rica, aduana Guatemala, aduana Peru, restricciones envios Peru"
        />
        <link
          rel="canonical"
          href="https://elpaket.com/articulos-prohibidos"
        />
      </Helmet>

      <Nav />

      <main className="prohibited-page-main">
        {/* Header */}
        <div className="prohibited-page-header">
          <p className="header-brand">elPaket</p>
          <h1>Artículos Prohibidos y Restricciones</h1>
        </div>

        <div className="prohibited-content-container">
          {/* Sección 1: Prohibiciones Universales (Tope) */}
          <section className="prohibited-universal-section">
            <div className="section-header-title">
              <FontAwesomeIcon icon={faGlobe} className="section-icon-main" />
              <h2>Prohibiciones Universales (Normativa IATA y Aduanas)</h2>
            </div>
            <p className="section-intro">
              Artículos que <strong>NUNCA</strong> deben enviarse por canales
              regulares de Courier Aéreo por razones de seguridad aeronáutica y
              leyes internacionales.
            </p>

            <div className="universal-grid">
              {universalProhibitions.map((item, index) => (
                <div key={index} className="universal-card">
                  <div className="universal-card-header">
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="prohibited-red-icon"
                    />
                    <h3>{item.category}</h3>
                  </div>
                  <div className="universal-card-body">
                    <div className="air-block">
                      <span className="channel-badge air-badge">
                        <FontAwesomeIcon icon={faPlane} /> Vía Aérea
                      </span>
                      <ul>
                        {item.air.map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="maritime-block">
                      <span className="channel-badge ship-badge">
                        <FontAwesomeIcon icon={faShip} /> Alternativa Marítima
                      </span>
                      <p>{item.maritime}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Sección 2: Restricciones por País */}
          <section className="prohibited-countries-section">
            <div className="section-header-title">
              <FontAwesomeIcon
                icon={faWarning}
                className="section-icon-main warning-orange"
              />
              <h2>Restricciones Específicas por País Destino</h2>
            </div>
            <p className="section-intro">
              Haz clic en el país correspondiente para ver sus regulaciones
              aduanales de importación aérea y marítima.
            </p>

            <div className="countries-accordion-container">
              {countriesData.map((country, index) => {
                const isOpen = openCountry === country.name;
                return (
                  <div
                    key={index}
                    className={`country-accordion-item ${isOpen ? "active" : ""}`}
                  >
                    <div
                      className="country-accordion-header"
                      onClick={(e) => {
                        handleToggleCountry(country.name);
                        if (!isOpen) {
                          const targetElement = e.currentTarget.parentElement;
                          setTimeout(() => {
                            targetElement?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }, 260);
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="country-title-group">
                        <span className="country-flag">
                          <img
                            src={`https://flagcdn.com/w40/${country.code}.png`}
                            srcSet={`https://flagcdn.com/w80/${country.code}.png 2x`}
                            width="28"
                            height="18"
                            alt={country.name}
                            className="country-flag-img-accordion"
                            loading="lazy"
                          />
                        </span>
                        <h3>{country.name}</h3>
                      </div>
                      <FontAwesomeIcon
                        icon={isOpen ? faChevronUp : faChevronDown}
                        className="accordion-arrow"
                      />
                    </div>
                    <div className="country-accordion-content-wrapper">
                      <div className="country-accordion-content-inner">
                        <div className="country-accordion-body">
                          <div className="responsive-table-wrapper">
                            {country.code === "pe" ? (
                              <table className="restrictions-table">
                                <thead>
                                  <tr>
                                    <th>Rubro / Categoría</th>
                                    <th>Tipo de Artículo / Producto</th>
                                    <th>Estado Legal</th>
                                    <th>Entidad Reguladora</th>
                                    <th>
                                      Requisitos / Restricciones Específicas
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {country.peruRestrictions?.map(
                                    (rest, rIdx) => (
                                      <tr key={rIdx}>
                                        <td className="col-category">
                                          {rest.category}
                                        </td>
                                        <td>{rest.productType}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                          <span
                                            className={`status-badge status-${rest.legalStatus.toLowerCase()}`}
                                          >
                                            {rest.legalStatus}
                                          </span>
                                        </td>
                                        <td>{rest.regulator}</td>
                                        <td>{rest.requirements}</td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </table>
                            ) : (
                              <table className="restrictions-table">
                                <thead>
                                  <tr>
                                    <th>Rubro / Categoría</th>
                                    <th>
                                      <FontAwesomeIcon icon={faPlane} /> Vía
                                      Aérea
                                    </th>
                                    <th>
                                      <FontAwesomeIcon icon={faShip} /> Vía
                                      Marítima
                                    </th>
                                    <th>
                                      <FontAwesomeIcon icon={faBan} />{" "}
                                      Prohibición Absoluta
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {country.restrictions?.map((rest, rIdx) => (
                                    <tr key={rIdx}>
                                      <td className="col-category">
                                        {rest.category}
                                      </td>
                                      <td className="col-air">
                                        {rest.air && rest.air.length > 0 ? (
                                          <ul>
                                            {rest.air.map((line, lIdx) => (
                                              <li key={lIdx}>{line}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <span className="no-restriction">
                                            -
                                          </span>
                                        )}
                                      </td>
                                      <td className="col-maritime">
                                        {rest.maritime &&
                                        rest.maritime.length > 0 ? (
                                          <ul>
                                            {rest.maritime.map((line, lIdx) => (
                                              <li key={lIdx}>{line}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <span className="no-restriction">
                                            -
                                          </span>
                                        )}
                                      </td>
                                      <td className="col-absolute">
                                        {rest.absolute &&
                                        rest.absolute.length > 0 ? (
                                          <ul className="absolute-list">
                                            {rest.absolute.map((line, lIdx) => (
                                              <li key={lIdx}>
                                                <FontAwesomeIcon
                                                  icon={faTimes}
                                                  className="ban-icon"
                                                />{" "}
                                                {line}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <span className="no-restriction">
                                            -
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>{" "}
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA (Sección de Contáctanos) - Mantenida como solicitó el usuario */}
          <div className="prohibited-cta">
            <h3>¿Tienes dudas sobre algún artículo?</h3>
            <p>Contáctanos antes de enviar y te asesoraremos sin compromiso.</p>
            <div className="prohibited-cta-buttons">
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "17548005041"}`}
                className="btn-whatsapp-prohibited"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faPhone} /> Consultar por WhatsApp
              </a>
              <Link to="/" className="btn-back-prohibited">
                <FontAwesomeIcon icon={faArrowLeft} /> Volver al Inicio
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      {/*
        <FloatingButton />
        */}
    </>
  );
};

export default ProhibitedItemsPage;
